import { composeCleanup } from '../../helpers/cleanup'
import {
  isOfficeKind,
  isOfficeResult,
  type OfficeKind,
  type OfficeResult
} from './types'

// Private types ---------------------------------------------------------------

type PreviewState =
  | { readonly status: 'idle' }
  | {
      readonly status: 'loading'
      readonly url: string
      readonly controller: AbortController
    }
  | { readonly status: 'ready'; readonly url: string }
type PreviewController = {
  readonly sync: () => void
  readonly cancel: () => void
  readonly cleanup: () => void
}
type OfficeHook = {
  readonly el: HTMLElement
  preview?: PreviewController
}

// Constants -------------------------------------------------------------------

const MAX_FILE_BYTES = 10 * 1024 * 1024
const TIMEOUT_MS = 20_000

// Private functions -----------------------------------------------------------

// Stops conversion on cancellation, timeout, failure, or a completed result.
const convertOffice = (
  kind: OfficeKind,
  buffer: ArrayBuffer,
  signal: AbortSignal
): Promise<OfficeResult> =>
  new Promise((resolve, reject) => {
    signal.throwIfAborted()
    const worker = new Worker(
      new URL('./office-preview-worker.js', import.meta.url),
      { type: 'module' }
    )
    const cancel = (): void =>
      finish(() => reject(new Error('Preview canceled')))
    const timer = window.setTimeout(cancel, TIMEOUT_MS)
    const cleanup = composeCleanup(
      () => window.clearTimeout(timer),
      () => signal.removeEventListener('abort', cancel),
      () => worker.terminate()
    )
    const finish = (complete: () => void): void => {
      cleanup()
      complete()
    }
    worker.onmessage = (event: MessageEvent<unknown>): void => {
      const result = event.data
      finish(() =>
        isOfficeResult(result)
          ? resolve(result)
          : reject(new Error('Invalid preview result'))
      )
    }
    worker.onerror = (): void =>
      finish(() => reject(new Error('Preview worker failed')))
    signal.addEventListener('abort', cancel, { once: true })
    worker.postMessage({ kind, buffer }, [buffer])
  })

// Rejects oversized responses before retaining or parsing their full contents.
const loadOffice = async (
  url: string,
  signal: AbortSignal
): Promise<ArrayBuffer> => {
  const response = await fetch(url, { signal, credentials: 'omit' })
  if (!(response.ok && response.body)) {
    throw new Error('Attachment unavailable')
  }
  const reader = response.body.getReader()
  const chunks: Uint8Array<ArrayBuffer>[] = []
  let size = 0
  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) {
        break
      }
      size += value.byteLength
      if (size > MAX_FILE_BYTES) {
        throw new Error('Attachment too large')
      }
      chunks.push(value)
    }
  } finally {
    await reader.cancel()
  }

  return new Blob(chunks).arrayBuffer()
}

// Owns the client-only preview surface while LiveView owns the source attributes.
const setupPreview = (host: HTMLElement): PreviewController => {
  const dialog = host.closest('wa-dialog')
  const frame = host.querySelector('iframe')
  const status = host.querySelector<HTMLElement>('[data-part="status"]')
  const notice = host.querySelector<HTMLElement>('[data-part="notice"]')
  const lifetime = new AbortController()
  let state: PreviewState = { status: 'idle' }

  // Closing or disconnecting invalidates all later asynchronous writes.
  const cancel = (): void => {
    if (state.status === 'loading') {
      state.controller.abort()
      state = { status: 'idle' }
    }
  }

  // Fetches and converts only the attachment whose dialog is currently open.
  const start = async (): Promise<void> => {
    const { url, kind } = host.dataset
    if (
      !(
        dialog?.hasAttribute('open') &&
        url &&
        isOfficeKind(kind) &&
        frame &&
        status &&
        notice
      ) ||
      state.status !== 'idle'
    ) {
      return
    }
    const controller = new AbortController()
    state = { status: 'loading', url, controller }
    frame.hidden = true
    notice.hidden = true
    status.hidden = false
    status.textContent = host.dataset.loading ?? ''
    try {
      const [buffer, { officeDocument }] = await Promise.all([
        loadOffice(url, controller.signal),
        import('./document')
      ])
      controller.signal.throwIfAborted()
      const result = await convertOffice(kind, buffer, controller.signal)
      controller.signal.throwIfAborted()
      if (result.status === 'error') {
        throw new Error('Cannot preview this attachment')
      }
      frame.srcdoc = officeDocument(
        result.html,
        new URL('/assets/app.css', location.href)
      )
      frame.hidden = false
      status.hidden = true
      notice.hidden = !result.truncated
      state = { status: 'ready', url }
    } catch {
      if (!controller.signal.aborted) {
        status.textContent = host.dataset.error ?? ''
        state = { status: 'idle' }
      }
    }
  }

  // Refreshes changed signed URLs without replacing a preview during ordinary patches.
  const sync = (): void => {
    if (state.status !== 'idle' && state.url !== host.dataset.url) {
      cancel()
      state = { status: 'idle' }
    }
    start()
  }
  dialog?.addEventListener('wa-after-show', sync, { signal: lifetime.signal })
  dialog?.addEventListener('wa-hide', cancel, { signal: lifetime.signal })
  sync()

  return {
    sync,
    cancel,
    cleanup: composeCleanup(cancel, () => lifetime.abort())
  }
}

// Public hook -----------------------------------------------------------------

export const FacadeOfficePreview = {
  // Binds lazy conversion to the preview dialog's lifetime.
  mounted(this: OfficeHook): void {
    this.preview = setupPreview(this.el)
  },
  // Reconciles server-owned source attributes after patches.
  updated(this: OfficeHook): void {
    this.preview?.sync()
  },
  // Cancels storage requests and parsing during a connection interruption.
  disconnected(this: OfficeHook): void {
    this.preview?.cancel()
  },
  // Restarts an interrupted preview only if its dialog remains open.
  reconnected(this: OfficeHook): void {
    this.preview?.sync()
  },
  // Releases requests, workers, timers, and dialog listeners on removal.
  destroyed(this: OfficeHook): void {
    this.preview?.cleanup()
  }
}
